/**
 * AdminVoiceBankRecorder.jsx
 *
 * The calm, in-app recorder for Michael's canonical library.
 *
 * Zero spreadsheets. One line at a time. Skip anything, come back
 * whenever. Progress persists — everything already recorded is
 * automatically hidden by default.
 *
 * Route: /admin/voice-bank-recorder  (admin only)
 * Backends:
 *   · GET  /api/admin/canonical-affirmations/progress
 *   · GET  /api/admin/canonical-affirmations/list
 *   · GET  /api/admin/canonical-affirmations/summary  (for theme filter)
 *   · POST /api/user-voice-bank/record
 *   · GET  /api/user-voice-bank                    (to preview mine)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../AdminPages";
import {
  ArrowLeft, Mic, Square, Play, Pause, RotateCcw, Check,
  SkipForward, Volume2, ChevronRight, Loader2, ListFilter,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

const SECTION_LABELS = {
  truth: "Truth",
  nothing_missing: "Nothing Missing",
  trust: "Trust",
  identity: "Identity",
  performance: "Performance",
  final_loop: "Final Loop",
};

async function fetchJSON(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { ...auth(), ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

/* ------------------------------- Recorder ------------------------------- */

function useRecorder() {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [state, setState] = useState("idle"); // idle | recording | ready
  const [blob, setBlob] = useState(null);
  const [mime, setMime] = useState("audio/webm");
  const [error, setError] = useState("");

  const start = useCallback(async () => {
    setError("");
    setBlob(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a supported mime; Safari prefers mp4/aac, Chrome/Firefox webm/opus.
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/mpeg",
      ];
      const chosen = candidates.find(
        (m) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(m)
      ) || "";
      const mr = chosen ? new MediaRecorder(stream, { mimeType: chosen })
                        : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const type = mr.mimeType || chosen || "audio/webm";
        const b = new Blob(chunksRef.current, { type });
        setBlob(b);
        setMime(type);
        setState("ready");
        // release mic
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setState("recording");
    } catch (e) {
      setError(e?.message || "mic_denied");
      setState("idle");
    }
  }, []);

  const stop = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
  }, []);

  const reset = useCallback(() => {
    setBlob(null);
    setState("idle");
    setError("");
  }, []);

  return { state, blob, mime, error, start, stop, reset };
}

/* ------------------------------- Page ------------------------------- */

export default function AdminVoiceBankRecorder() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [themes, setThemes] = useState([]);
  const [themeFilter, setThemeFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [queue, setQueue] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [loading, setLoading] = useState(false);
  const [savingState, setSavingState] = useState("idle"); // idle | saving | saved | error
  const [saveError, setSaveError] = useState("");
  const [previewingLine, setPreviewingLine] = useState(false);
  const audioElRef = useRef(null);
  const takeAudioRef = useRef(null);

  const current = queue[cursor] || null;

  const rec = useRecorder();

  const loadProgress = useCallback(async () => {
    try {
      const data = await fetchJSON("/api/admin/canonical-affirmations/progress");
      setProgress(data);
    } catch { /* silent */ }
  }, []);

  const loadThemes = useCallback(async () => {
    try {
      const data = await fetchJSON("/api/admin/canonical-affirmations/summary");
      setThemes(data.rows || []);
    } catch { /* silent */ }
  }, []);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: "15",
        only_missing: onlyMissing ? "true" : "false",
      });
      if (themeFilter) params.set("theme_id", themeFilter);
      if (sectionFilter) params.set("section", sectionFilter);
      const data = await fetchJSON(`/api/admin/canonical-affirmations/list?${params.toString()}`);
      setQueue(data.items || []);
      setCursor(0);
      rec.reset();
    } catch {
      setQueue([]);
    } finally {
      setLoading(false);
    }
  }, [themeFilter, sectionFilter, onlyMissing, rec]);

  useEffect(() => { loadProgress(); loadThemes(); }, [loadProgress, loadThemes]);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  // Preview cloned-voice version of the current line (uses the on-demand
  // TTS cache; near-zero cost after first play).
  const previewLine = useCallback(async () => {
    if (!current) return;
    setPreviewingLine(true);
    try {
      const url = `${API}/api/audio-journeys/read-aloud?` +
        new URLSearchParams({ text: current.text });
      // read-aloud may redirect to a cached MP3; use audio element.
      const el = audioElRef.current;
      if (!el) return;
      el.src = url + "&t=" + Date.now();
      await el.play().catch(() => {});
    } finally {
      setPreviewingLine(false);
    }
  }, [current]);

  const playTake = useCallback(() => {
    const el = takeAudioRef.current;
    if (!el || !rec.blob) return;
    el.src = URL.createObjectURL(rec.blob);
    el.play().catch(() => {});
  }, [rec.blob]);

  const saveTake = useCallback(async () => {
    if (!current || !rec.blob) return;
    setSavingState("saving");
    setSaveError("");
    try {
      const fd = new FormData();
      fd.append("text", current.text);
      const ext = (rec.mime || "audio/webm").includes("mp4") ? "mp4"
                : (rec.mime || "audio/webm").includes("mpeg") ? "mp3"
                : "webm";
      fd.append("audio", rec.blob, `take.${ext}`);
      const res = await fetch(`${API}/api/user-voice-bank/record`, {
        method: "POST",
        headers: auth(),
        body: fd,
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setSavingState("saved");
      // Advance to next line after a tiny beat so the checkmark reads.
      setTimeout(() => {
        setSavingState("idle");
        rec.reset();
        setCursor((c) => c + 1);
        loadProgress();
        // If we've walked off the end, refresh the queue.
        if (cursor + 1 >= queue.length) loadQueue();
      }, 600);
    } catch (e) {
      setSavingState("error");
      setSaveError(e?.message || "save_failed");
    }
  }, [current, rec, cursor, queue.length, loadProgress, loadQueue]);

  const skip = useCallback(() => {
    rec.reset();
    if (cursor + 1 >= queue.length) loadQueue();
    else setCursor((c) => c + 1);
  }, [rec, cursor, queue.length, loadQueue]);

  /* ---------- render ---------- */

  const overall = useMemo(() => {
    if (!progress) return { pct: 0, recorded: 0, total: 0 };
    return {
      pct: progress.percent ?? 0,
      recorded: progress.recorded ?? 0,
      total: progress.total ?? 0,
    };
  }, [progress]);

  return (
    <AdminLayout>
      <div
        data-testid="admin-voice-bank-recorder"
        className="mx-auto max-w-3xl px-4 sm:px-6 py-8"
      >
        {/* Header */}
        <button
          onClick={() => navigate("/admin")}
          className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700 mb-6"
          data-testid="back-to-admin"
        >
          <ArrowLeft className="w-4 h-4" /> Admin
        </button>

        <h1 className="text-4xl sm:text-5xl font-serif tracking-tight text-stone-900">
          Voice Bank
        </h1>
        <p className="mt-3 text-stone-600 max-w-xl">
          One line at a time. Skip anything, come back whenever. Nothing you
          record today is wasted — it just deepens the library.
        </p>

        {/* Progress */}
        <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-5">
          <div className="flex items-baseline justify-between">
            <div className="text-sm text-stone-500">Your voice, across the library</div>
            <div className="text-sm text-stone-700 font-medium" data-testid="progress-count">
              {overall.recorded.toLocaleString()} / {overall.total.toLocaleString()}
            </div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-stone-100 overflow-hidden">
            <div
              className="h-full bg-[#A88A4C] transition-all duration-500"
              style={{ width: `${Math.min(100, overall.pct)}%` }}
              data-testid="progress-bar"
            />
          </div>
          <div className="mt-2 text-xs text-stone-400">
            {overall.pct.toFixed(1)}% of canonical lines carry your real voice
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-stone-500">
            <ListFilter className="w-4 h-4" /> Focus
          </div>
          <select
            value={themeFilter}
            onChange={(e) => setThemeFilter(e.target.value)}
            className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm text-stone-800"
            data-testid="filter-theme"
          >
            <option value="">All themes</option>
            {themes.map((t) => (
              <option key={t.theme_id} value={t.theme_id}>{t.label} ({t.total})</option>
            ))}
          </select>
          <select
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm text-stone-800"
            data-testid="filter-section"
          >
            <option value="">All sections</option>
            {Object.entries(SECTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <label className="ml-2 inline-flex items-center gap-2 text-sm text-stone-600">
            <input
              type="checkbox"
              checked={onlyMissing}
              onChange={(e) => setOnlyMissing(e.target.checked)}
              data-testid="filter-only-missing"
            />
            Only unrecorded
          </label>
        </div>

        {/* Recorder card */}
        <div className="mt-8 rounded-3xl border border-stone-200 bg-gradient-to-b from-white to-stone-50 p-6 sm:p-10 shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-stone-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading next lines…
            </div>
          ) : !current ? (
            <div className="text-center py-16">
              <div className="text-2xl font-serif text-stone-900">All caught up.</div>
              <p className="mt-2 text-stone-600 max-w-md mx-auto">
                Nothing left in this filter. Widen the theme, or turn off
                &ldquo;Only unrecorded&rdquo; to revisit takes.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs uppercase tracking-widest text-stone-400">
                <span data-testid="current-meta">
                  {current.theme_label} · {SECTION_LABELS[current.section] || current.section}
                </span>
                <span>{cursor + 1} of {queue.length}</span>
              </div>

              <p
                className="mt-6 font-serif text-2xl sm:text-3xl leading-snug text-stone-900"
                data-testid="current-line-text"
              >
                {current.text}
              </p>

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={previewLine}
                  disabled={previewingLine}
                  className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                  data-testid="preview-cloned"
                >
                  <Volume2 className="w-4 h-4" />
                  {previewingLine ? "Loading…" : "Hear cloned voice"}
                </button>
              </div>

              <audio ref={audioElRef} preload="none" />
              <audio ref={takeAudioRef} preload="none" />

              {/* Controls */}
              <div className="mt-10 flex flex-col items-center">
                {rec.state === "idle" && !rec.blob && (
                  <button
                    onClick={rec.start}
                    className="inline-flex items-center gap-3 rounded-full bg-[#A88A4C] px-8 py-4 text-white text-lg font-medium shadow-lg hover:bg-[#96793e] active:scale-95 transition"
                    data-testid="record-start"
                  >
                    <Mic className="w-5 h-5" /> Record
                  </button>
                )}
                {rec.state === "recording" && (
                  <button
                    onClick={rec.stop}
                    className="inline-flex items-center gap-3 rounded-full bg-red-500 px-8 py-4 text-white text-lg font-medium shadow-lg animate-pulse"
                    data-testid="record-stop"
                  >
                    <Square className="w-5 h-5" /> Stop
                  </button>
                )}
                {rec.state === "ready" && rec.blob && (
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <button
                      onClick={playTake}
                      className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                      data-testid="playback-take"
                    >
                      <Play className="w-4 h-4" /> Play back
                    </button>
                    <button
                      onClick={() => { rec.reset(); }}
                      className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                      data-testid="record-retake"
                    >
                      <RotateCcw className="w-4 h-4" /> Retake
                    </button>
                    <button
                      onClick={saveTake}
                      disabled={savingState === "saving"}
                      className="inline-flex items-center gap-2 rounded-full bg-[#0F1B2D] px-6 py-2.5 text-sm text-white hover:bg-[#1a2b45] disabled:opacity-60"
                      data-testid="save-take"
                    >
                      {savingState === "saving" && <Loader2 className="w-4 h-4 animate-spin" />}
                      {savingState === "saved" && <Check className="w-4 h-4" />}
                      {savingState !== "saving" && savingState !== "saved" && <Check className="w-4 h-4" />}
                      {savingState === "saving" ? "Saving…"
                        : savingState === "saved" ? "Saved"
                        : "Save & next"}
                    </button>
                  </div>
                )}
                {rec.error && (
                  <div className="mt-4 text-sm text-red-600" data-testid="record-error">
                    Mic error: {rec.error}. Grant microphone access and try again.
                  </div>
                )}
                {savingState === "error" && (
                  <div className="mt-4 text-sm text-red-600" data-testid="save-error">
                    Save failed ({saveError}). Try again.
                  </div>
                )}
              </div>

              {/* Skip */}
              <div className="mt-8 flex justify-center">
                <button
                  onClick={skip}
                  className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800"
                  data-testid="skip-line"
                >
                  Skip for now <SkipForward className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Per-theme progress */}
        {progress && progress.rows && progress.rows.length > 0 && (
          <div className="mt-10">
            <div className="text-sm text-stone-500 mb-3">By theme</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {progress.rows.map((r) => {
                const pct = r.total ? (r.recorded / r.total) * 100 : 0;
                return (
                  <button
                    key={r.theme_id}
                    onClick={() => setThemeFilter(r.theme_id)}
                    className="text-left rounded-xl border border-stone-200 bg-white p-3 hover:border-stone-300"
                    data-testid={`theme-row-${r.theme_id}`}
                  >
                    <div className="flex items-baseline justify-between">
                      <div className="text-sm font-medium text-stone-800">{r.label}</div>
                      <div className="text-xs text-stone-500">
                        {r.recorded} / {r.total}
                      </div>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                      <div
                        className="h-full bg-[#A88A4C]"
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
